import { useCallback, useEffect, useMemo, useState } from "react";

import { TInputTag } from "components/InputMultipleTag";

import {
  useGetClientPost,
  useGetCorporationList,
  useGetDepartment,
} from "modules/MemberList/UseCase";

import { TClientFilter } from "modules/MemberList/types";

interface UseClientFilterProps {
  filter?: Partial<TClientFilter>;
  onFilter?: (filter: Partial<TClientFilter>) => void;
}

const useClientFilter = ({ filter, onFilter }: UseClientFilterProps) => {
  const [clientPostTags, setClientPostTags] = useState<TInputTag[]>([]);
  const [departementTags, setDepartementTags] = useState<TInputTag[]>([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [corporationTags, setCorporationTags] = useState<TInputTag[]>([]);

  const isEmptyFilter = useMemo(() => {
    const existingFilter: Record<string, string | undefined> = {
      corp: filter?.corp,
      department: filter?.department,
      post: filter?.post,
    };

    for (const key in existingFilter) {
      if (existingFilter?.[key]) return false;
    }

    return true;
  }, [filter]);

  const { corporationOptions, isLoadingCorporationOptions } =
    useGetCorporationList({
      enabled: openFilter,
    });

  const { departmentOptions, isloadingDepartmentOptions } = useGetDepartment({
    enabled: openFilter,
  });

  const { isLoadingClientClientPost, postOptions } = useGetClientPost({
    enabled: openFilter,
  });

  const handleOpenFilter = () => {
    setOpenFilter((prev) => !prev);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleSubmitFilter = () => {
    onFilter?.({
      corp: corporationTags?.map((c) => c.id)?.join(","),
      department: departementTags?.map((dept) => dept.id)?.join(","),
      post: clientPostTags?.map((post) => post.id)?.join(","),
    });
    setOpenFilter(false);
  };

  const handleResetFilter = useCallback(() => {
    setClientPostTags([]);
    setDepartementTags([]);
    setCorporationTags([]);

    onFilter?.({
      corp: undefined,
      department: undefined,
      post: undefined,
    });
  }, [onFilter]);

  const handleCloseAndResetFilter = useCallback(() => {
    handleCloseFilter();
    handleResetFilter();
  }, [handleResetFilter]);

  useEffect(() => {
    setCorporationTags(
      constructFilterFromParam(corporationOptions, filter?.corp)
    );
  }, [corporationOptions, filter]);

  useEffect(() => {
    setClientPostTags(constructFilterFromParam(postOptions, filter?.post));
  }, [postOptions, filter]);

  useEffect(() => {
    setDepartementTags(
      constructFilterFromParam(departmentOptions, filter?.department)
    );
  }, [departmentOptions, filter]);

  return {
    openFilter,
    clientPostTags,
    departementTags,
    isLoading:
      isLoadingClientClientPost ||
      isloadingDepartmentOptions ||
      isLoadingCorporationOptions,

    isEmptyFilter,
    corporationOptions,
    corporationTags,
    departmentOptions,
    postOptions,
    setCorporationTags,
    setClientPostTags,
    setDepartementTags,
    onOpenFilter: handleOpenFilter,
    onCloseFilter: handleCloseFilter,
    onSubmitFilter: handleSubmitFilter,
    onResetFilter: handleResetFilter,
    onCloseAndResetFilter: handleCloseAndResetFilter,
  };
};

export default useClientFilter;

export const constructFilterFromParam = (
  tags: TInputTag[],
  filterParams?: string
) => {
  if (!filterParams || !tags?.length) return [];
  const params = filterParams.split(",");
  const filteredTags = tags.filter((tag) => params.includes(tag.id));

  return filteredTags.map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));
};
